"use client";

import { byBrand } from "@repo/tailwind-config/brand";
import Image from "next/image";
import React from "react";
import kinsaHeaderImage from "public/kinsa-header.svg";
import { BodyText, HeadlineBodyText } from "src/components/KinsaTypography";
import { SUPPORT_EMAIL } from "src/lib/product-name";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

function OwletError({ error, reset }: ErrorProps) {
  return (
    <div className="flex min-h-[calc(100vh-var(--felt-safe-area-top,0px)-var(--felt-safe-area-bottom,0px))] flex-col bg-[#060727]">
      <header className="flex flex-col items-center gap-2 px-4 pt-[calc(var(--felt-safe-area-top,0px)+16px)]">
        <Image
          src="/icons/owlet/logo.png"
          alt="Owlet OnCall"
          height={18}
          width={128}
          priority
        />
        <p className="text-center font-source-sans text-xs font-semibold leading-4 text-[#7d80e6]">
          Pediatricians 24/7 in under an hour
        </p>
      </header>

      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-[var(--felt-safe-area-bottom,0px)]">
        <div className="flex w-[345px] max-w-full flex-col items-center gap-6">
          <Image
            src="/unplugged-icon.svg"
            alt=""
            width={129}
            height={86}
            priority
          />
          <h1 className="text-center font-source-serif text-xl font-semibold leading-6 text-[#d4d4f9]">
            Something went wrong
          </h1>
          <p className="text-center font-source-sans text-base font-normal leading-6 text-[#d4d4f9]">
            We couldn&apos;t load your information at the moment. Please close
            the app and try again. If the issue continues, contact support at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
          {error.message && (
            <p className="max-w-full text-center font-mono text-xs leading-4 text-[#d4d4f9]/50 break-words">
              {error.message}
            </p>
          )}
          <button
            onClick={reset}
            className="font-source-sans text-base font-semibold leading-6 text-[#7d80e6]"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

function KinsaError({ error, reset }: ErrorProps) {
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="flex items-center justify-center pt-[calc(var(--felt-safe-area-top,0px)+16px)] pb-4 border-b-[0.5px] border-slate-100">
        <Image src={kinsaHeaderImage} alt="Kinsa Logo" height={24} priority />
      </header>

      <div className="flex flex-1 flex-col items-center justify-center px-6 pb-[var(--felt-safe-area-bottom,0px)] text-center">
        <div className="flex w-full max-w-sm flex-col items-center gap-4">
          <HeadlineBodyText className="text-text-300">
            Something went wrong
          </HeadlineBodyText>
          <BodyText className="text-text-200 leading-relaxed">
            We couldn&apos;t load your information at the moment. Please close
            the app and try again. If the issue continues, contact support at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </BodyText>
          {error.message && (
            <BodyText className="text-text-300 text-sm leading-relaxed">
              {error.message}
            </BodyText>
          )}
          <button
            onClick={reset}
            className="mt-2 rounded bg-primary px-4 py-2 text-white"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Error(props: ErrorProps) {
  return byBrand({
    owlet: <OwletError {...props} />,
    default: <KinsaError {...props} />,
  });
}
